import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

/**
 * PB-4923 AC5 — the seam a leaf control uses to say "somebody is editing THIS
 * field, right now".
 *
 * The widget frame reads `beginFieldEdit` off the entity handle it already
 * holds and provides it here, so a control deep inside an editor needs neither
 * the widget id nor the resolver — one hook, no prop drilling. That is what
 * makes it practical to wire the shared inputs rather than only the top-level
 * editors.
 *
 * Data-blind by construction: one opaque string in, one teardown function out.
 * Nothing in this package learns about HTTP, expiry, heartbeats or tab
 * identity; the host owns all of it.
 */
export interface WidgetFieldEditSession {
  /** Claim `targetKey`; the returned function releases that one claim. */
  readonly begin: (targetKey: string) => () => void;
}

const Ctx = createContext<WidgetFieldEditSession | null>(null);

export const WidgetFieldEditSessionProvider = Ctx.Provider;

export function useWidgetFieldEditSession(): WidgetFieldEditSession | null {
  return useContext(Ctx);
}

/**
 * Bind one control's focus lifetime to a field editing session.
 *
 * Returns `onFocus` / `onBlur` handlers to spread onto the input. The claim is
 * released on blur AND on unmount, because a modal that closes while a field is
 * focused never fires blur — and a lease nobody released would then keep an AI
 * change away from a field nobody is in front of, for the whole server TTL.
 *
 * A `targetKey` of `undefined` is the "this control has no addressable target"
 * case (an editor that has not been wired yet, a control bound to something the
 * merge register does not name). It leases nothing rather than guessing, which
 * matches how the diff reports an unrepresentable change.
 */
export function useFieldEditSession(targetKey: string | undefined): {
  readonly onFocus: () => void;
  readonly onBlur: () => void;
} {
  const session = useWidgetFieldEditSession();
  const releaseRef = useRef<(() => void) | null>(null);
  const focusedRef = useRef(false);

  const release = (): void => {
    const current = releaseRef.current;
    releaseRef.current = null;
    current?.();
  };

  // Unmount is the case blur cannot cover. Deliberately keyed on nothing: the
  // teardown must run when the control goes away, whatever the target was.
  useEffect(() => {
    if (
      focusedRef.current &&
      releaseRef.current === null &&
      session !== null &&
      targetKey !== undefined
    ) {
      releaseRef.current = session.begin(targetKey);
    }
    return release;
  }, [session, targetKey]);

  return {
    onFocus: () => {
      focusedRef.current = true;
      if (session === null || targetKey === undefined) return;
      // Re-focusing without an intervening blur (a re-render that restores
      // focus) must not stack two claims the host has to reference-count.
      if (releaseRef.current !== null) return;
      releaseRef.current = session.begin(targetKey);
    },
    onBlur: () => {
      focusedRef.current = false;
      release();
    },
  };
}

/**
 * The same lease, for a control set whose size is not known at compile time.
 *
 * `useFieldEditSession` is one hook per control, which is right for a fixed
 * field list and unusable for a grid: the N×M cells of `EditableDataGrid` are
 * rendered in a loop inside ONE component, so they cannot each own a hook. This
 * returns a FACTORY over identical semantics — one claim per target, no
 * stacking on re-focus, every claim released on unmount — so a collection
 * editor shares this implementation of the rules instead of growing a second
 * copy that can drift out of step with it.
 *
 * The factory is also what a data-blind molecule can be handed as a plain prop:
 * the host resolves `(rowId, columnId)` to a canonical target key and passes
 * the handlers down, so the molecule never learns the addressing grammar.
 */
export function useFieldEditSessionMap(): (targetKey: string | undefined) => {
  readonly onFocus: () => void;
  readonly onBlur: () => void;
} {
  const session = useWidgetFieldEditSession();
  const releasesRef = useRef(new Map<string, () => void>());

  // Same reason as the single-control hook: a modal that closes over a focused
  // cell never fires blur, and an unreleased claim would fence an AI change out
  // of a field nobody is in front of until the server TTL expires.
  useEffect(() => {
    const held = releasesRef.current;
    return () => {
      for (const release of held.values()) release();
      held.clear();
    };
  }, []);

  return useCallback(
    (targetKey: string | undefined) => {
      const release = (): void => {
        if (targetKey === undefined) return;
        const current = releasesRef.current.get(targetKey);
        if (current === undefined) return;
        releasesRef.current.delete(targetKey);
        current();
      };
      return {
        onFocus: () => {
          if (session === null || targetKey === undefined) return;
          if (releasesRef.current.has(targetKey)) return;
          releasesRef.current.set(targetKey, session.begin(targetKey));
        },
        onBlur: release,
      };
    },
    [session],
  );
}
